// <s-signup-popup> opens its signup dialog once per visit (browser session)
// after a delay, and reopens after a signup post-back to show the result.
const DEFAULT_STORAGE_KEY = 'db-signup-popup-seen';

class SSignupPopup extends HTMLElement {
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    const designMode = this.dataset.designMode === 'true';
    const postedBack = this.querySelector('[data-signup-success], [data-signup-error]');

    // Show the success code or form errors straight after submitting.
    if (postedBack) {
      this.#markSeen();
      this.#open(dialog);
      return;
    }

    // Manual popups (sold-out pages for categories that aren't set up yet) open only when the
    // page confirms it is a category address, and remember each address separately.
    if (this.dataset.manual === 'true') {
      const start = () => {
        this.dataset.storageKey = `${this.#storageKey}-${window.location.pathname}`;
        if (!designMode && this.#seenThisVisit()) return;
        this.#timer = setTimeout(() => {
          if (!designMode) this.#markSeen();
          this.#open(dialog);
        }, Math.max(0, Number(this.dataset.delay) || 0) * 1000);
      };
      if (document.documentElement.dataset.soldOutPage === 'true') start();
      else document.addEventListener('db:sold-out-page', start, { once: true });
      return;
    }

    if (!designMode && this.#seenThisVisit()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = setTimeout(() => {
      if (!designMode) this.#markSeen();
      this.#open(dialog);
    }, delay);
  }

  get #storageKey() {
    return this.dataset.storageKey || DEFAULT_STORAGE_KEY;
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #open(dialog) {
    if (dialog.open) return;
    // Another popup (such as the trade-in thank-you) is showing: wait until it closes, then open.
    const other = document.querySelector('dialog:modal');
    if (other) {
      other.addEventListener('close', () => this.#open(dialog), { once: true });
      return;
    }
    dialog.showModal();
  }

  #seenThisVisit() {
    try {
      return sessionStorage.getItem(this.#storageKey) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      sessionStorage.setItem(this.#storageKey, '1');
    } catch {
      // Storage can be blocked; the popup then shows on each page instead.
    }
  }
}

if (!customElements.get('s-signup-popup')) {
  customElements.define('s-signup-popup', SSignupPopup);
}
